import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import HelpArticleService from '../../../services/helpArticleService';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Modal from '../../../components/ui/Modal';

const HelpArticlesCMS = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingArticle, setDeletingArticle] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [notification, setNotification] = useState(null);

  // Categories
  const categories = [
    { id: 'all', label: 'All Categories' },
    { id: 'getting-started', label: 'Getting Started' },
    { id: 'profile', label: 'Profile & Settings' },
    { id: 'jobs', label: 'Jobs & Applications' },
    { id: 'messaging', label: 'Communication' },
    { id: 'payment', label: 'Payment & Billing' },
    { id: 'technical', label: 'Technical Issues' },
    { id: 'policies', label: 'Policies & Guidelines' }
  ];

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin' && userProfile.role !== 'moderator') {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Load articles and stats
  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await HelpArticleService.getAllArticles();
      if (result.success) {
        setArticles(result.data);
      } else {
        showNotification('error', 'Failed to load articles');
      }

      // Load stats
      const statsResult = await HelpArticleService.getArticleStats();
      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading articles:', error);
      showNotification('error', 'An error occurred while loading articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  // Filter articles
  useEffect(() => {
    let filtered = articles;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    // Filter by status
    if (selectedStatus === 'published') {
      filtered = filtered.filter(article => article.is_published);
    } else if (selectedStatus === 'draft') {
      filtered = filtered.filter(article => !article.is_published);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.description?.toLowerCase().includes(query) ||
        article.tags?.some(tag => tag.toLowerCase().includes(query))
      );
    }

    setFilteredArticles(filtered);
  }, [articles, selectedCategory, selectedStatus, searchQuery]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateArticle = () => {
    navigate('/admin-moderation?tab=help-cms&mode=create');
  };

  const handleEditArticle = (article) => {
    navigate(`/admin-moderation?tab=help-cms&mode=edit&articleId=${article.id}`);
  };

  const handleDeleteArticle = (article) => {
    setDeletingArticle(article);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingArticle) return;

    try {
      const result = await HelpArticleService.deleteArticle(deletingArticle.id);
      if (result.success) {
        showNotification('success', 'Article deleted successfully');
        loadArticles();
      } else {
        showNotification('error', result.error || 'Failed to delete article');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while deleting');
    } finally {
      setShowDeleteModal(false);
      setDeletingArticle(null);
    }
  };

  const handleTogglePublish = async (article) => {
    try {
      const result = await HelpArticleService.togglePublishStatus(
        article.id,
        !article.is_published
      );
      
      if (result.success) {
        showNotification(
          'success',
          article.is_published ? 'Article unpublished' : 'Article published'
        );
        loadArticles();
      } else {
        showNotification('error', result.error || 'Failed to update status');
      }
    } catch (error) {
      showNotification('error', 'An error occurred while updating');
    }
  };

  const handleSelectArticle = (articleId) => {
    setSelectedArticles(prev =>
      prev.includes(articleId)
        ? prev.filter(id => id !== articleId)
        : [...prev, articleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === filteredArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(filteredArticles.map(a => a.id));
    }
  };

  const handleBulkPublish = async () => {
    if (selectedArticles.length === 0) return;

    try {
      const result = await HelpArticleService.bulkUpdatePublishStatus(selectedArticles, true);
      if (result.success) {
        showNotification('success', `${selectedArticles.length} articles published`);
        setSelectedArticles([]);
        loadArticles();
      } else {
        showNotification('error', 'Failed to publish articles');
      }
    } catch (error) {
      showNotification('error', 'An error occurred');
    }
  };

  const handleBulkUnpublish = async () => {
    if (selectedArticles.length === 0) return;

    try {
      const result = await HelpArticleService.bulkUpdatePublishStatus(selectedArticles, false);
      if (result.success) {
        showNotification('success', `${selectedArticles.length} articles unpublished`);
        setSelectedArticles([]);
        loadArticles();
      } else {
        showNotification('error', 'Failed to unpublish articles');
      }
    } catch (error) {
      showNotification('error', 'An error occurred');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedArticles.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedArticles.length} articles?`)) {
      return;
    }

    try {
      const result = await HelpArticleService.bulkDeleteArticles(selectedArticles);
      if (result.success) {
        showNotification('success', `${selectedArticles.length} articles deleted`);
        setSelectedArticles([]);
        loadArticles();
      } else {
        showNotification('error', 'Failed to delete articles');
      }
    } catch (error) {
      showNotification('error', 'An error occurred');
    }
  };

  const getStatusBadge = (isPublished) => {
    return isPublished ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Icon name="Check" size={12} className="mr-1" />
        Published
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Icon name="Clock" size={12} className="mr-1" />
        Draft
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const colors = {
      high: 'bg-red-100 text-red-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    };

    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${colors[urgency]}`}>
        {urgency}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <main className="pt-16 pb-20 md:pb-8">
          <div className="content-container">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-8 pb-20 md:pb-8">
        <div>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-text-primary">
                  Help Articles CMS
                </h1>
                <p className="text-text-secondary mt-1">
                  Manage help and support content
                </p>
              </div>
              <Button
                variant="primary"
                iconName="Plus"
                onClick={handleCreateArticle}
              >
                Create Article
              </Button>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-secondary">Total Articles</p>
                      <p className="text-2xl font-bold text-text-primary">{stats.total}</p>
                    </div>
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={24} className="text-primary" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-secondary">Published</p>
                      <p className="text-2xl font-bold text-green-600">{stats.published}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <Icon name="Check" size={24} className="text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-secondary">Total Views</p>
                      <p className="text-2xl font-bold text-text-primary">{stats.totalViews}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Icon name="Eye" size={24} className="text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-text-secondary">Helpful Votes</p>
                      <p className="text-2xl font-bold text-text-primary">{stats.totalHelpfulVotes}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Icon name="Star" size={24} className="text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg border border-border p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="relative">
                  <Icon
                    name="Search"
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search articles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.label}
                    </option>
                  ))}
                </select>

                {/* Status Filter */}
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Status</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                </select>
              </div>
            </div>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg ${
              notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              <div className="flex items-center">
                <Icon
                  name={notification.type === 'success' ? 'Check' : 'AlertCircle'}
                  size={20}
                  className="mr-2"
                />
                <span>{notification.message}</span>
              </div>
            </div>
          )}

          {/* Bulk Actions */}
          {selectedArticles.length > 0 && (
            <div className="mb-4 bg-primary-50 border border-primary-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-800">
                  {selectedArticles.length} article(s) selected
                </span>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Check"
                    onClick={handleBulkPublish}
                  >
                    Publish
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="X"
                    onClick={handleBulkUnpublish}
                  >
                    Unpublish
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    iconName="Trash2"
                    onClick={handleBulkDelete}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Articles Table */}
          <div className="bg-white rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedArticles.length === filteredArticles.length && filteredArticles.length > 0}
                        onChange={handleSelectAll}
                        className="rounded text-primary focus:ring-primary"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Urgency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Views
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Helpful
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredArticles.map(article => (
                    <tr key={article.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedArticles.includes(article.id)}
                          onChange={() => handleSelectArticle(article.id)}
                          className="rounded text-primary focus:ring-primary"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="flex items-center cursor-pointer"
                          role="button"
                          tabIndex={0}
                          onClick={() => handleEditArticle(article)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleEditArticle(article); } }}
                        >
                          <Icon name={article.icon_name} size={20} className="mr-3 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {article.title}
                            </div>
                            <div className="text-sm text-gray-500">
                              {article.slug}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-900 capitalize">
                          {article.category.replace('-', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(article.is_published)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getUrgencyBadge(article.urgency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {article.view_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {article.helpful_count || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleTogglePublish(article)}
                            className="text-gray-600 hover:text-primary"
                            title={article.is_published ? 'Unpublish' : 'Publish'}
                          >
                            <Icon name={article.is_published ? 'EyeOff' : 'Eye'} size={18} />
                          </button>
                          <button
                            onClick={() => handleEditArticle(article)}
                            className="text-gray-600 hover:text-primary"
                            title="Edit"
                          >
                            <Icon name="Edit" size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteArticle(article)}
                            className="text-gray-600 hover:text-red-600"
                            title="Delete"
                          >
                            <Icon name="Trash2" size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty State */}
            {filteredArticles.length === 0 && (
              <div className="text-center py-12">
                <Icon name="FileText" size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  No articles found
                </h3>
                <p className="text-text-secondary mb-4">
                  {searchQuery || selectedCategory !== 'all' || selectedStatus !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Get started by creating your first article'}
                </p>
                {!searchQuery && selectedCategory === 'all' && selectedStatus === 'all' && (
                  <Button
                    variant="primary"
                    iconName="Plus"
                    onClick={handleCreateArticle}
                  >
                    Create First Article
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeletingArticle(null);
        }}
        title="Delete Article"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Are you sure you want to delete "{deletingArticle?.title}"? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setDeletingArticle(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              iconName="Trash2"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default HelpArticlesCMS;
